import { defineStore } from 'pinia';
import { blocsApi, type Bloc, type CreateBlocDto } from '@api/blocs.api';
import { useToast } from '../composables/useToast';
import { useHypothesesStore } from './hypotheses.store';

export const useBlocsStore = defineStore('blocs', {
  state: () => ({
    blocs: [] as Bloc[],
    selectedBloc: null as Bloc | null,
    loading: false,
    error: null as string | null,
  }),

  actions: {
    async fetchAll(search?: string) {
      this.loading = true;
      this.error = null;
      try {
        this.blocs = await blocsApi.getAll(search);
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors du chargement des blocs';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[BlocsStore] Error fetching blocs:', error);
        }
      } finally {
        this.loading = false;
      }
    },

    async create(data: CreateBlocDto, image: File) {
      this.loading = true;
      this.error = null;
      const { success, error: showError } = useToast();
      try {
        const newBloc = await blocsApi.create(data, image);
        this.blocs.push(newBloc);
        success('Bloc importé avec succès');
        return newBloc;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la création du bloc';
        this.error = message;
        showError(message);
        if (import.meta.env.DEV) {
          console.error('[BlocsStore] Error creating bloc:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async update(id: number, data: Partial<CreateBlocDto>) {
      this.loading = true;
      this.error = null;
      try {
        const updatedBloc = await blocsApi.update(id, data);
        const index = this.blocs.findIndex((b) => b.id === id);
        if (index !== -1) {
          this.blocs[index] = updatedBloc;
        }
        if (this.selectedBloc?.id === id) {
          this.selectedBloc = updatedBloc;
        }
        // Garder le canvas à jour : nested `bloc` dans l'hypothèse n'était pas rechargé après édition.
        const h = useHypothesesStore();
        const cur = h.currentHypothesis;
        if (cur?.blocks?.length) {
          h.currentHypothesis = {
            ...cur,
            blocks: cur.blocks.map((hb) => {
              if (hb.bloc_id !== id || !hb.bloc) return hb;
              return {
                ...hb,
                bloc: {
                  ...hb.bloc,
                  id: updatedBloc.id,
                  object_name: updatedBloc.object_name,
                  inventory_number: updatedBloc.inventory_number,
                  description: updatedBloc.description,
                  fragment_emplacement: updatedBloc.fragment_emplacement,
                  fragment_identification: updatedBloc.fragment_identification,
                  poids_estime_kg: updatedBloc.poids_estime_kg,
                  image_path: updatedBloc.image_path,
                  scale_px_per_mm: updatedBloc.scale_px_per_mm,
                  image_width: updatedBloc.image_width,
                  image_height: updatedBloc.image_height,
                  real_size_x_mm: updatedBloc.real_size_x_mm,
                  real_size_y_mm: updatedBloc.real_size_y_mm,
                  real_size_z_mm: updatedBloc.real_size_z_mm,
                },
              };
            }),
          };
        }
        return updatedBloc;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la mise à jour du bloc';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[BlocsStore] Error updating bloc:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async delete(id: number) {
      this.loading = true;
      this.error = null;
      try {
        await blocsApi.delete(id);
        this.blocs = this.blocs.filter((b) => b.id !== id);
        if (this.selectedBloc?.id === id) {
          this.selectedBloc = null;
        }
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la suppression du bloc';
        this.error = message;
        if (import.meta.env.DEV) {
          console.error('[BlocsStore] Error deleting bloc:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    setSelectedBloc(bloc: Bloc | null) {
      this.selectedBloc = bloc;
    },

    async createAvatar(sourceBlocId: number, color: string) {
      this.loading = true;
      this.error = null;
      const { success, error: showError } = useToast();
      try {
        const avatarBloc = await blocsApi.createAvatar(sourceBlocId, color);
        this.blocs.unshift(avatarBloc);
        success('Avatar créé avec succès');
        return avatarBloc;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la création de l'avatar";
        this.error = message;
        showError(message);
        if (import.meta.env.DEV) {
          console.error('[BlocsStore] Error creating avatar:', error);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
